/**
 * useAttributeEngine — fetches Attribute Engine definitions + industry-specific config
 *
 * Usage (no industry filter — shows all attributes):
 *   const { attributes, byGroup, toLookupOptions, loading } = useAttributeEngine();
 *
 * Usage (industry-filtered — shows only attributes configured for that industry,
 *         in the configured order, with per-industry custom labels):
 *   const { industryAttrs, industryByGroup, toLookupOptions, loading } = useAttributeEngine({ industryId: 'Apparel' });
 *
 * Country mandatory:
 *   const { mandatoryCountries } = useAttributeEngine();
 *   // mandatoryCountries['hsn'] = ['IND'] means HSN is required in India
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE, getToken } from '../lib/api';

const hdrs = () => ({ Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

const CONTROL_TYPE_MAP = {
  LookUpEdit: 'select', ComboBoxEdit: 'select', CalcEdit: 'number', SpinEdit: 'number',
  DateEdit: 'date', CheckEdit: 'checkbox', TextEdit: 'text', MemoEdit: 'text',
};
const COL_WIDTHS = {
  LookUpEdit: 110, ComboBoxEdit: 90, CalcEdit: 80, SpinEdit: 70, DateEdit: 100, TextEdit: 100,
};

export function useAttributeEngine({ industryId, skip = false } = {}) {
  const [attributes, setAttributes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [lookupValues, setLookupValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Industry-specific: the ordered+labelled attributes for the chosen industry
  const [industryAttrs, setIndustryAttrs] = useState([]);
  // Country mandatory map: { attrId: [countryCode, ...] }
  const [mandatoryCountries, setMandatoryCountries] = useState({});

  // ── Fetch base attributes, groups, lookup values (once) ──────────────────────
  useEffect(() => {
    // When skip=true (snapshot available in screen def), bypass all API calls
    if (skip) { setLoading(false); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const [rAttrs, rGroups, rMandatory] = await Promise.all([
          fetch(`${API_BASE}/api/attribute-engine/attributes`, { headers: hdrs() }),
          fetch(`${API_BASE}/api/attribute-engine/groups`, { headers: hdrs() }),
          fetch(`${API_BASE}/api/attribute-engine/country-mandatory`, { headers: hdrs() }),
        ]);
        const j = await rAttrs.json();
        const gj = await rGroups.json();
        const mj = await rMandatory.json();
        if (!j.success) throw new Error(j.error || 'Failed to load attributes');
        const allAttrs = j.data || [];
        if (cancelled) return;
        setAttributes(allAttrs);
        setGroups((gj.success ? gj.data : null) || []);
        setMandatoryCountries(mj.success ? (mj.data || {}) : {});

        // Fetch lookup values (LookUpEdit/ComboBoxEdit with SourceTable) in parallel chunks
        const lookupAttrs = allAttrs.filter(a =>
          (a.ControlType === 'LookUpEdit' || a.ControlType === 'ComboBoxEdit') && a.SourceTable
        );
        const valMap = {};
        const chunks = [];
        for (let i = 0; i < lookupAttrs.length; i += 6) chunks.push(lookupAttrs.slice(i, i + 6));
        for (const chunk of chunks) {
          await Promise.all(chunk.map(async (attr) => {
            try {
              const vr = await fetch(`${API_BASE}/api/attribute-engine/attributes/${attr.AttributeId}/values`, { headers: hdrs() });
              const vj = await vr.json();
              valMap[attr.AttributeId] = vj.success ? (vj.data || []) : [];
            } catch { valMap[attr.AttributeId] = []; }
          }));
        }
        // Parse OptionsJson for ComboBoxEdit without SourceTable
        allAttrs.filter(a => a.ControlType === 'ComboBoxEdit' && a.OptionsJson && !a.SourceTable).forEach(attr => {
          try {
            const opts = JSON.parse(attr.OptionsJson);
            valMap[attr.AttributeId] = opts.map(o =>
              typeof o === 'string' ? { id: o, name: o } : { id: o.value || o.id, name: o.label || o.name }
            );
          } catch { valMap[attr.AttributeId] = []; }
        });
        if (!cancelled) setLookupValues(valMap);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [skip]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch industry-specific ordered+labelled attrs when industryId changes ───
  useEffect(() => {
    if (skip || !industryId) { setIndustryAttrs([]); return; }
    let cancelled = false;
    fetch(`${API_BASE}/api/attribute-engine/industries/${industryId}/attributes`, { headers: hdrs() })
      .then(r => r.json())
      .then(j => {
        if (!cancelled && j.success) {
          // Replace Label with EffectiveLabel (CustomLabel if set, else BaseLabel)
          setIndustryAttrs((j.data || []).map(a => ({ ...a, Label: a.EffectiveLabel || a.Label })));
        }
      })
      .catch(() => { if (!cancelled) setIndustryAttrs([]); });
    return () => { cancelled = true; };
  }, [industryId, skip]);

  /** Convert a list of attribute IDs to ERPEditableGrid column definitions */
  const toGridColumns = useCallback((attributeIds) => {
    return attributeIds
      .map(id => attributes.find(a => a.AttributeId === id))
      .filter(Boolean)
      .map(attr => {
        const col = {
          field: attr.AttributeId, header: attr.Label,
          width: COL_WIDTHS[attr.ControlType] || 100,
          type: CONTROL_TYPE_MAP[attr.ControlType] || 'text',
        };
        if (col.type === 'select') {
          col.options = (lookupValues[attr.AttributeId] || []).map(v => ({ value: String(v.id), label: v.name }));
        }
        return col;
      });
  }, [attributes, lookupValues]);

  /** Get <select> options for a specific attribute */
  const toLookupOptions = useCallback((attrId, { valueKey = 'id', labelKey = 'name' } = {}) => {
    const vals = lookupValues[attrId] || [];
    return vals.map(v => ({ value: String(v[valueKey] ?? v.id), label: v[labelKey] ?? v.name }));
  }, [lookupValues]);

  /** Get attribute definition by ID */
  const getAttr = useCallback((attrId) => attributes.find(a => a.AttributeId === attrId) || null, [attributes]);

  /** All attributes grouped by GroupId, sorted by DisplayOrder */
  const byGroup = useMemo(() => {
    const map = {};
    attributes.forEach(a => {
      const gid = a.GroupId || '(none)';
      if (!map[gid]) map[gid] = [];
      map[gid].push(a);
    });
    Object.values(map).forEach(g => g.sort((a, b) => (a.DisplayOrder || 99) - (b.DisplayOrder || 99)));
    return map;
  }, [attributes]);

  /** Industry-filtered attributes grouped by GroupId (uses EffectiveLabel, industry DisplayOrder) */
  const industryByGroup = useMemo(() => {
    if (!industryAttrs.length) return {}; // return empty until industry data loads (avoids flash of all attrs)
    const map = {};
    industryAttrs.forEach(a => {
      const gid = a.GroupId || '(none)';
      if (!map[gid]) map[gid] = [];
      map[gid].push(a);
    });
    return map;
  }, [industryAttrs]);

  /** Industry-filtered groups (ordered by group DisplayOrder) */
  const industryGroups = useMemo(() => {
    if (!industryAttrs.length) return groups;
    const gids = new Set(industryAttrs.map(a => a.GroupId || '(none)'));
    return groups.filter(g => gids.has(g.GroupId));
  }, [industryAttrs, groups]);

  /** All attributes that have an ItemMasterColumn */
  const itemMasterAttrs = useMemo(() =>
    attributes.filter(a => a.ItemMasterColumn && a.GroupId && a.GroupId !== '(none)')
      .sort((a, b) => (a.DisplayOrder || 99) - (b.DisplayOrder || 99)),
    [attributes]
  );

  const toItemGridColumns = useCallback(() =>
    toGridColumns(itemMasterAttrs.map(a => a.AttributeId)),
    [toGridColumns, itemMasterAttrs]
  );

  /** Get attribute list for a specific GroupId */
  const getGroup = useCallback((groupId) => byGroup[groupId] || [], [byGroup]);

  /**
   * Check if an attribute is required in a given country code.
   * countryCode: ISO-3 string e.g. 'IND', 'ARE', 'USA'
   */
  const isRequiredInCountry = useCallback((attrId, countryCode) => {
    return (mandatoryCountries[attrId] || []).includes(countryCode);
  }, [mandatoryCountries]);

  return {
    attributes, groups, lookupValues, loading, error,
    toGridColumns, toLookupOptions, getAttr,
    byGroup, itemMasterAttrs, toItemGridColumns, getGroup,
    // Industry-specific exports
    industryAttrs, industryByGroup, industryGroups,
    // Country mandatory
    mandatoryCountries, isRequiredInCountry,
  };
}
